import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import Spinner from './Spinner'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 버튼 스타일 변형 */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  /** 버튼 크기 */
  size?: 'sm' | 'md' | 'lg'
  /** 전체 너비 여부 */
  fullWidth?: boolean
  /** 로딩 상태 (Spinner 표시) */
  loading?: boolean
}

const variantStyles = {
  primary:
    'bg-gradient-to-b from-cb-primary to-cb-primary-hover text-white shadow-[var(--shadow-button)] hover:brightness-110 hover:shadow-md active:brightness-95 active:shadow-sm disabled:from-cb-gray-300 disabled:to-cb-gray-300 disabled:shadow-none',
  secondary:
    'bg-white text-cb-primary border border-cb-gray-200 shadow-sm hover:bg-cb-primary-light hover:border-cb-primary active:bg-cb-primary-muted disabled:border-cb-gray-200 disabled:text-cb-gray-400',
  ghost:
    'bg-transparent text-cb-gray-700 hover:bg-cb-gray-100 active:bg-cb-gray-200 disabled:text-cb-gray-400',
  danger:
    'bg-gradient-to-b from-cb-danger to-red-600 text-white shadow-[var(--shadow-button)] hover:brightness-110 active:brightness-95 disabled:from-cb-gray-300 disabled:to-cb-gray-300',
} as const

const sizeStyles = {
  sm: 'h-[2.25rem] px-3 text-body-sm font-medium rounded-[var(--radius-button)]',
  md: 'h-[2.75rem] px-4 text-body font-medium rounded-[var(--radius-button)]',
  lg: 'h-[3.25rem] px-6 text-body font-bold rounded-[var(--radius-button)]',
} as const

/**
 * 버튼 컴포넌트.
 *
 * - variant: primary(블루), secondary(보더), ghost(투명), danger(빨강)
 * - size: sm(36px), md(44px), lg(52px CTA)
 * - loading 상태에서 Spinner 표시
 * - 접근성: aria-label, disabled, aria-busy(loading)
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-sans transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
          'disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading && <Spinner size={size === 'sm' ? 'sm' : 'md'} />}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
